import React, { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ticketsApi } from '@/api/tickets.api';
import { Modal } from '@/components/ui/Modal';
import { Field } from '@/components/ui/Field';
import { ApiError } from '@/lib/errors';
import { useToasts } from '@/context/ToastContext';
import { COST_REGIONS, COST_REGION_SYMBOL } from '@/types/domain';
import { invalidateAfterTicketAction } from '@/lib/queryInvalidation';

interface ResolutionValues {
  issue_note: string | null;
  cost_region: string | null;
  cost_amount: number | null;
  payment_mode: string | null;
  remarks: string | null;
}

interface Props {
  ticketId: string;
  open: boolean;
  onClose: () => void;
  current: ResolutionValues;
}

/**
 * V4.9.26 — Edit the solver "Resolution details" (Issues / Cost / Payment mode
 * / Remarks). All optional, editable ANYTIME, independent of adding a note.
 */
export function ResolutionModal({ ticketId, open, onClose, current }: Props) {
  const [issueNote, setIssueNote] = useState('');
  const [costRegion, setCostRegion] = useState('');
  const [costAmount, setCostAmount] = useState('');
  const [paymentMode, setPaymentMode] = useState('');
  const [remarks, setRemarks] = useState('');
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { push } = useToasts();

  // Prefill from the ticket each time the modal opens.
  useEffect(() => {
    if (!open) return;
    setError(null);
    setIssueNote(current.issue_note ?? '');
    setCostRegion(current.cost_region ?? '');
    setCostAmount(current.cost_amount != null ? String(current.cost_amount) : '');
    setPaymentMode(current.payment_mode ?? '');
    setRemarks(current.remarks ?? '');
  }, [open, current]);

  const mutation = useMutation({
    mutationFn: async () => {
      const amountNum = costAmount.trim() === '' ? undefined : Number(costAmount);
      return ticketsApi.updateResolution(ticketId, {
        issue_note: issueNote,
        cost_region: costRegion || '',
        cost_amount: costRegion && amountNum != null && !Number.isNaN(amountNum) ? amountNum : undefined,
        payment_mode: paymentMode,
        remarks
      });
    },
    onSuccess: () => {
      push({ kind: 'success', title: 'Resolution details saved' });
      invalidateAfterTicketAction(queryClient, ticketId);
      onClose();
    },
    onError: (err: unknown) => {
      setError(
        err instanceof ApiError ? err.problem?.detail ?? err.message : 'Failed to save resolution details'
      );
    }
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Resolution details"
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? 'Saving...' : 'Save'}
          </button>
        </>
      }
    >
      <p className="text-small text-muted" style={{ marginTop: 0 }}>
        All optional — fill in what you have. You can update these anytime.
      </p>

      <Field label="Issues">
        <textarea
          className="form-input"
          rows={2}
          value={issueNote}
          onChange={(e) => setIssueNote(e.target.value)}
          placeholder="What was the issue?"
        />
      </Field>

      <Field label="Cost" hint="Pick the region, then enter the amount in that currency.">
        <div className="cost-row">
          <select
            className="form-select"
            value={costRegion}
            onChange={(e) => setCostRegion(e.target.value)}
            aria-label="Cost region"
          >
            <option value="">Region…</option>
            {COST_REGIONS.map((r) => (
              <option key={r} value={r}>
                {r} ({COST_REGION_SYMBOL[r].trim()})
              </option>
            ))}
          </select>
          <div className="cost-amount-wrap">
            <span className="cost-symbol">{costRegion ? COST_REGION_SYMBOL[costRegion].trim() : '$'}</span>
            <input
              className="form-input cost-amount-input"
              type="number"
              min="0"
              step="0.01"
              inputMode="decimal"
              value={costAmount}
              onChange={(e) => setCostAmount(e.target.value)}
              placeholder="0.00"
              disabled={!costRegion}
              aria-label="Cost amount"
            />
          </div>
        </div>
      </Field>

      <Field label="Payment mode">
        <input
          className="form-input"
          value={paymentMode}
          onChange={(e) => setPaymentMode(e.target.value)}
          placeholder="e.g. Store credit, Card refund, Bank transfer"
          maxLength={60}
        />
      </Field>

      <Field label="Remarks">
        <textarea
          className="form-input"
          rows={2}
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder="Any extra remarks"
        />
      </Field>

      {error ? <div className="form-error mt-2">{error}</div> : null}
    </Modal>
  );
}
